import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as apiLogin, fetchPermissions, apiResetPassword, signUp as apiSignUp, apiForgotPassword, fetchUserSites } from '../services/auth';
import { canCreateSiteApi } from '../services/sites';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [currentSiteId, setCurrentSiteId] = useState(null);
  const [userSites, setUserSites] = useState([]);
  const [canCreateSite, setCanCreateSite] = useState(false);
  const navigate = useNavigate();

  const loadPermissions = async (siteId) => {
    const userPermissions = await fetchPermissions(siteId);
    setPermissions(userPermissions);
    const canCreate = await canCreateSiteApi();
    setCanCreateSite(canCreate);
  };

  const changeSiteId = async (newSiteId) => {
    if (!userSites?.find(site => site.site_id === newSiteId)) {
      throw new Error('Invalid site ID');
    }
    await loadPermissions(newSiteId);
    setCurrentSiteId(newSiteId);
    localStorage.setItem('lastSiteId', newSiteId.toString());
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      const lastSiteId = localStorage.getItem('lastSiteId');
      
      if (storedUser && storedToken && window.location.pathname !== '/login') {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setToken(storedToken);

        // Fetch user sites
        const sites = await fetchUserSites();
        setUserSites(sites);

        // Determine which site ID to use
        let siteId = lastSiteId ? parseInt(lastSiteId) : null;
        if (!sites?.find(site => site.site_id === siteId)) {
          siteId = sites?.[0]?.site_id;
        }
        
        if (siteId !== undefined) {
          setCurrentSiteId(siteId);
          localStorage.setItem('lastSiteId', siteId.toString());
          await loadPermissions(siteId);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const userData = await apiLogin(credentials);
      const authToken = userData.token;
      // Decode the JWT token to get user information
      const tokenPayload = JSON.parse(atob(authToken.split('.')[1]));
      const { sub: email, id, name } = tokenPayload;
      userData.email = email;
      userData.userId = id; 
      userData.name = name;
      setUser(userData);
      setToken(authToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', authToken);
      // Fetch user sites
      const sites = await fetchUserSites();
      setUserSites(sites);

      const siteId = sites?.[0]?.site_id;
      if (siteId !== undefined) {
        setCurrentSiteId(siteId);
        localStorage.setItem('lastSiteId', siteId.toString());
        await loadPermissions(siteId);
      }
      //navigate('/dashboard');
      return true;
    } catch (error) {
      throw new Error('Invalid credentials.');
    }
  };

  const sign_up = async (credentials) => {
    try {
      const userData = await apiSignUp(credentials);
      const authToken = userData.token;
      // Decode the JWT token to get user information
      const tokenPayload = JSON.parse(atob(authToken.split('.')[1]));
      const { sub: email, id, name } = tokenPayload;
      userData.email = email;
      userData.userId = id; 
      userData.name = name;
      setUser(userData);
      setToken(authToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', authToken);
      // Fetch user sites
      const sites = await fetchUserSites();
      setUserSites(sites);

      const siteId = sites?.[0]?.site_id;
      if (siteId !== undefined) {
        setCurrentSiteId(siteId);
        localStorage.setItem('lastSiteId', siteId.toString());
        await loadPermissions(siteId);
      }
      return true;
    } catch (error) {
      console.error('Sign up error:', error);
      if (error.message === 'Account already exists') {
        throw new Error('Account already exists');
      }
      throw new Error('Sign up failed');
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setPermissions([]);
    setCurrentSiteId(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('lastSiteId');
    navigate('/login');
  };

  const resetPassword = async (newPassword) => {
    try {
      if (!user || !token) {
        throw new Error('User not authenticated');
      }
      await apiResetPassword(newPassword);
      // Optionally, you can update the stored token if it changes after password reset
      // const newToken = await apiResetPassword(user.id, newPassword, token);
      // setToken(newToken);
      // localStorage.setItem('token', newToken);
      return true;
    } catch (error) {
      console.error('Reset password error:', error);
      return false;
    }
  };

  const forgotPassword = async (email) => {
    try {
      await apiForgotPassword(email);
      return true;
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error.message === 'User account not found') {
        throw new Error('User account not found');
      }
      throw new Error('Password reset failed');
    }
  };

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      token, 
      permissions, 
      currentSiteId,
      canCreateSite,
      changeSiteId,
      userSites,
      login, 
      sign_up, 
      logout, 
      hasPermission, 
      resetPassword, 
      forgotPassword 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);